import { useEffect, useState } from 'react';
import { api, type ListResult } from '../api';

interface Line {
  itemType: string;
  productId?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
}

interface Quote {
  id: string;
  quoteNumber: string;
  status: string;
  currency: string;
  total: unknown;
  accountId: string | null;
  terms: string | null;
  lineItems: { description: string | null; quantity: unknown; unitPrice: unknown; discount: unknown }[];
}

interface Product {
  id: string;
  name: string;
  unitPrice: unknown;
}

const STATUSES = ['draft', 'sent', 'accepted', 'rejected', 'expired'];
const num = (v: unknown) => Number(v ?? 0);

export function QuotesPage() {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [editing, setEditing] = useState<Quote | 'new' | null>(null);
  const [accountId, setAccountId] = useState('');
  const [terms, setTerms] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [editId, setEditId] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);

  async function load() {
    const res = await api.get<ListResult<Quote>>('/api/quotes');
    setQuotes(res.items);
  }

  useEffect(() => {
    void api.get<ListResult<Product>>('/api/products').then((r) => setProducts(r.items)).catch(() => undefined);
    void load();
  }, []);

  function openNew() {
    setEditId(undefined);
    setAccountId('');
    setTerms('');
    setLines([{ itemType: 'custom', description: '', quantity: 1, unitPrice: 0, discount: 0 }]);
    setEditing('new');
  }

  function openEdit(q: Quote) {
    setEditId(q.id);
    setAccountId(q.accountId ?? '');
    setTerms(q.terms ?? '');
    setLines(
      q.lineItems.map((li) => ({
        itemType: 'custom',
        description: li.description ?? '',
        quantity: num(li.quantity),
        unitPrice: num(li.unitPrice),
        discount: num(li.discount),
      })),
    );
    setEditing(q);
  }

  function setLine(i: number, patch: Partial<Line>) {
    setLines((p) => p.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((p) => [...p, { itemType: 'custom', description: '', quantity: 1, unitPrice: 0, discount: 0 }]);
  }
  function addProduct(id: string) {
    const p = products.find((x) => x.id === id);
    if (!p) return;
    setLines((prev) => [...prev, { itemType: 'product', productId: p.id, description: p.name, quantity: 1, unitPrice: num(p.unitPrice), discount: 0 }]);
  }

  const totals = lines.reduce(
    (acc, l) => {
      acc.subtotal += l.quantity * l.unitPrice;
      acc.discount += l.discount;
      return acc;
    },
    { subtotal: 0, discount: 0 },
  );
  const total = totals.subtotal - totals.discount;

  async function save() {
    setError(null);
    const body = { accountId: accountId || undefined, terms: terms || undefined, lineItems: lines };
    try {
      if (editId) await api.patch(`/api/quotes/${editId}`, body);
      else await api.post('/api/quotes', body);
      setEditing(null);
      await load();
    } catch {
      setError('Save failed.');
    }
  }

  async function setStatus(q: Quote, status: string) {
    await api.post(`/api/quotes/${q.id}/status`, { status });
    await load();
  }
  async function convert(q: Quote) {
    try {
      const po = await api.post<{ poNumber: string }>(`/api/purchase-orders/from-quote/${q.id}`);
      window.alert(`Created ${po.poNumber}`);
    } catch {
      window.alert('Convert failed — the quote must be "accepted" first.');
    }
  }

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1>Quotes</h1>
        <button className="btn btn-primary" onClick={openNew}>+ New quote</button>
      </div>

      <table className="grid">
        <thead><tr><th>Number</th><th>Status</th><th>Total</th><th></th></tr></thead>
        <tbody>
          {quotes.map((q) => (
            <tr key={q.id}>
              <td>{q.quoteNumber}</td>
              <td>
                <select value={q.status} onChange={(e) => void setStatus(q, e.target.value)}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </td>
              <td>{q.currency} {num(q.total).toFixed(2)}</td>
              <td className="row">
                <button className="btn" onClick={() => openEdit(q)}>Edit</button>
                <a className="btn" href={`/api/quotes/${q.id}/pdf`} target="_blank" rel="noreferrer">PDF</a>
                <button className="btn" onClick={() => void convert(q)}>→ PO</button>
              </td>
            </tr>
          ))}
          {quotes.length === 0 && <tr><td colSpan={4} className="empty">No quotes yet.</td></tr>}
        </tbody>
      </table>

      {editing && (
        <div className="overlay" onClick={() => setEditing(null)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <h2>{editId ? 'Edit quote' : 'New quote'}</h2>
            {error && <p style={{ color: '#b91c1c' }}>{error}</p>}
            <label className="field"><span>Account ID</span><input value={accountId} onChange={(e) => setAccountId(e.target.value)} /></label>

            <div className="row" style={{ marginTop: '0.5rem' }}>
              <select defaultValue="" onChange={(e) => { if (e.target.value) addProduct(e.target.value); e.target.value = ''; }}>
                <option value="">Add product…</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <button className="btn" onClick={addLine}>+ Custom line</button>
            </div>

            <table className="grid">
              <thead><tr><th>Description</th><th>Qty</th><th>Unit</th><th>Disc</th><th></th></tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td><input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} /></td>
                    <td><input type="number" style={{ width: 60 }} value={l.quantity} onChange={(e) => setLine(i, { quantity: Number(e.target.value) })} /></td>
                    <td><input type="number" style={{ width: 80 }} value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: Number(e.target.value) })} /></td>
                    <td><input type="number" style={{ width: 70 }} value={l.discount} onChange={(e) => setLine(i, { discount: Number(e.target.value) })} /></td>
                    <td><button className="btn" onClick={() => setLines((p) => p.filter((_, idx) => idx !== i))}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="kv">
              <dt>Subtotal</dt><dd>{totals.subtotal.toFixed(2)}</dd>
              <dt>Discount</dt><dd>{totals.discount.toFixed(2)}</dd>
              <dt>Total</dt><dd>{total.toFixed(2)}</dd>
            </div>

            <label className="field"><span>Terms</span><textarea value={terms} onChange={(e) => setTerms(e.target.value)} /></label>
            <div className="row">
              <button className="btn btn-primary" onClick={() => void save()}>Save</button>
              <button className="btn" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
